import Head from "next/head";
import { useEffect, useMemo, useState } from "react";
import { fetchMenu } from "@/services/menuService";
import { capitalizeText, getImageUrl, showAds } from "@/utilities/menu";
import Footer from "@/components/layout/Footer";
import RatingStars from "@/components/rating/RatingStars";
import RatingForm from "@/components/rating/RatingForm";
import ReviewItem from "@/components/rating/ReviewItem";
import Link from "next/link";
import { Review } from "@/types/types";
import { fetchReviews, saveReview } from "@/services/reviewsService";
import AdSenseAd from "@/components/google-adSense/AdSenseAd";
import AdsComponent from "@/components/ads-component/AdsComponent";

export async function getServerSideProps(
  context: any
): Promise<{ props: PageProps }> {
  const { menu } = context.query;

  let menuData = null;

  if (menu) {
    const data = await fetchMenu(menu);
    if (data) {
      menuData = data;
    } else {
      return {
        notFound: true,
      } as any;
    }
  } else {
    return {
      notFound: true,
    } as any;
  }

  menuData.title = capitalizeText(menuData.siteName) + " Reviews";
  menuData.description = capitalizeText(menuData.siteName) + " Reviews";

  return {
    props: {
      menu: menuData,
    },
  };
}

interface PageProps {
  menu: any | null;
}

const ReviewsPage: React.FC<PageProps> = ({ menu }) => {
  const [showAddReview, setShowAddReview] = useState(false);
  const [reviews, setReviews] = useState<Partial<Review>[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  useEffect(() => {
    fetchReviews(menu?.siteName).then((reviews) => {
      if (reviews) {
        setReviews(reviews);
        setLoading(false);
      }
    });
  }, [menu]);

  const toggleAddReview = () => setShowAddReview((prev) => !prev);

  const menuName = useMemo(
    () => capitalizeText(menu?.siteName?.replace(/-/g, " ")),
    [menu]
  );
  const menuImage = useMemo(() => getImageUrl(menu?.imageMenu || ""), [menu]);

  const handleFormSubmit = async (review: Partial<Review>) => {
    setLoading(true);
    review.siteName = menu.siteName;
    saveReview(review)
      .then(() => {
        review.date = new Date().toDateString();
        if (review) {
          reviews.push(review);
        }
      })
      .catch(() => {
        alert("Error saving your review, try again later");
      })
      .finally(() => {
        setShowAddReview(false);
        setLoading(false);
      });
  };

  const averageRating =
    reviews.reduce((acc, review) => acc + (review.rating || 0), 0) /
    reviews.length;

  const shouldShowAds = showAds(menu);

  return (
    <>
      <Head>
        {/* add language here*/}
        <title>{menu.title}</title>
        <meta name="description" content={menu.description} />
        <link rel="icon" href="/favicon.ico" />
        <meta property="og:title" content={menu.title} />
        <meta property="og:description" content={menu.description} />
        {menuImage && <meta property="og:image" content={menuImage} />}
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={menu.title} />
        <meta name="twitter:description" content={menu.description} />
        <meta name="twitter:image" content={menu.ogImageUrl} />
      </Head>
      <div>
        <div
          className="h-56 sm:h-64 bg-center bg-cover bg-no-repeat "
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url('${menuImage}') `,
          }}
        ></div>
        <h1 className="mx-[6%] relative whitespace-nowrap overflow-hidden">
          {menu.title}
        </h1>
        <div className="flex flex-col min-h-screen bg-white font-inter rounded-tr-[70px] overflow-hidden mt-[-80px] relative">
          <div className="mx-[5%] bg-white flex justify-between items-center mt-[43px] ">
            <Link
              href={`/menus/${menu.siteName}`}
              onClick={(e) => {
                if (showAddReview) {
                  e.preventDefault();
                  setShowAddReview(false);
                }
              }}
            >
              <div className="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="36"
                  height="36"
                  viewBox="7 0 36 36"
                  fill="none"
                  style={{ minWidth: "36px" }}
                >
                  <path
                    d="M15.0823 10.4175L15.0823 16.4175L28.4623 16.4175L28.5073 19.4325L15.0823 19.4325L15.0823 25.4175L7.58232 17.9175L15.0823 10.4175Z"
                    fill="black"
                  />
                </svg>
                <h2 className="text-[22px] font-bold text-gray-900 leading-[30px]">
                  {showAddReview ? "Write a review" : "Reviews"}
                </h2>
              </div>
            </Link>
          </div>

          <div className="m-[5%]">
            <RatingStars rating={averageRating} onRatingChange={() => {}} />

            <div className="flex justify-between">
              {reviews?.length ? (
                <span className="text-[16px] font-[500] mt-2">
                  Based on {reviews?.length} reviews
                </span>
              ) : (
                <span className="text-[16px] font-[500] mt-2">
                  No reviews yet
                </span>
              )}
              {!showAddReview && (
                <button
                  className="text-[16px] font-[700] underline"
                  onClick={toggleAddReview}
                >
                  Write a review
                </button>
              )}
            </div>
          </div>

          {showAddReview ? (
            <RatingForm onSubmit={handleFormSubmit} />
          ) : (
            reviews.map((review, index) => (
              <ReviewItem key={index} review={review} />
            ))
          )}
          {/* Aff ads */}
          {shouldShowAds ? (
            <AdsComponent
              menuName={menu.siteName}
              country={menu.companyInfo?.country}
            />
          ) : (
            <></>
          )}

          {/* Google ads */}
          {shouldShowAds ? (
            <div className="mt-5 mb-[5%] mx-[5%]">
              <AdSenseAd
                adClient="ca-pub-9101961507466056"
                adSlot="9338909219"
                style={{
                  height: "300px",
                }} // Square dimensions
                format="auto"
                fullWidthResponsive="true"
              />
            </div>
          ) : (
            <></>
          )}

          {/* Bottom Restaurant Info Section */}
          <div className="m-[5%] py-2 bg-white">
            <h2 className="text-[22px] font-bold text-gray-900 leading-[30px]">
              {menuName}
            </h2>
            <span className="text-[15px] text-gray-900 leading-[10px]">
              {menu?.tagLine}
            </span>
          </div>

          {/* Footer Section */}
          <Footer siteName={menu.siteName} loadAds={shouldShowAds} />
        </div>
      </div>
    </>
  );
};
export default ReviewsPage;
